// Deadline urgency + formatting (Sprint P5 Section 4). Deadlines are stored at
// UTC-midnight date granularity, so all calendar math + display uses UTC to show
// the intended day on every client regardless of local timezone.

export type Urgency = 'overdue' | 'soon' | 'upcoming' | 'done';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function daysUntil(dueDateIso: string, now: Date = new Date()): number {
  const due = new Date(dueDateIso);
  const startDue = Date.UTC(due.getUTCFullYear(), due.getUTCMonth(), due.getUTCDate());
  const startNow = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((startDue - startNow) / MS_PER_DAY);
}

export function urgencyOf(dueDateIso: string, status: string, now: Date = new Date()): Urgency {
  if (status === 'DONE') {
    return 'done';
  }
  const d = daysUntil(dueDateIso, now);
  if (d < 0) {
    return 'overdue';
  }
  if (d <= 7) {
    return 'soon';
  }
  return 'upcoming';
}

/** Tailwind classes for a deadline by urgency. Colour is paired with text/label
 *  elsewhere so it is never the only signal. */
export function urgencyClasses(u: Urgency): string {
  switch (u) {
    case 'overdue':
      return 'border-red-900/50 bg-red-950/20 text-red-300';
    case 'soon':
      return 'border-amber-700/50 bg-amber-950/20 text-amber-300';
    case 'done':
      return 'border-stone-700 bg-stone-800/40 text-stone-500';
    default:
      return 'border-stone-800 bg-stone-900/40 text-stone-300';
  }
}

export function formatDueDate(dueDateIso: string): string {
  try {
    return new Date(dueDateIso).toLocaleDateString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  } catch {
    return dueDateIso.slice(0, 10);
  }
}

export function relativeDue(dueDateIso: string, status: string, now: Date = new Date()): string {
  if (status === 'DONE') {
    return 'done';
  }
  const d = daysUntil(dueDateIso, now);
  if (d < 0) {
    return `${Math.abs(d)}d overdue`;
  }
  if (d === 0) {
    return 'today';
  }
  if (d === 1) {
    return 'tomorrow';
  }
  return `in ${d}d`;
}
